import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, ExternalLink, Globe, ShoppingCart, Tag } from "lucide-react";
import { PRODUCTS } from "../test/catalog";
import { getStoredAppLocale, setStoredAppLocale } from "../../lib/app-locale";

const PAYMENT_LABELS = {
  ko: {
    label: "스토어",
    infoText: "네이버 스마트스토어",
    payButton: "스마트스토어에서 구매하기",
    backButton: "상품 목록으로",
    priceLabel: "판매가",
    featuresTitle: "상품 특징",
    imgAlt: "상세 이미지",
    thumbAlt: "썸네일",
    ariaPrev: "이전 이미지",
    ariaNext: "다음 이미지",
    notFoundTitle: "상품을 찾을 수 없습니다.",
    backToList: "목록으로 돌아가기",
    storeNote: "네이버 스마트스토어에서 결제와 배송이 진행됩니다.",
  },
  en: {
    label: "Store",
    infoText: "Naver Smart Store",
    payButton: "Buy on Naver Smart Store",
    backButton: "Back to Products",
    priceLabel: "Price",
    featuresTitle: "Features",
    imgAlt: "Image",
    thumbAlt: "Thumbnail",
    ariaPrev: "Previous image",
    ariaNext: "Next image",
    notFoundTitle: "Product not found.",
    backToList: "Back to list",
    storeNote: "Checkout and shipping are handled by Naver Smart Store.",
  },
};

function formatPrice(price, locale) {
  const formattedPrice = price.toLocaleString();
  return locale === "en" ? `₩${formattedPrice}` : `${formattedPrice}원`;
}

export default function PaymentProductDetailPage() {
  const navigate = useNavigate();
  const { productKey } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [locale, setLocale] = useState(getStoredAppLocale());

  const product = productKey ? PRODUCTS[productKey] : undefined;
  const labels = PAYMENT_LABELS[locale];

  const toggleLocale = () => {
    const newLocale = locale === "ko" ? "en" : "ko";
    setStoredAppLocale(newLocale);
    setLocale(newLocale);
  };

  // Resolve locale-specific detail images
  const rawDetailImages = product?.detailImages?.length ? product.detailImages : [product?.image].filter(Boolean);
  const detailImages = rawDetailImages.map((imgPath) => {
    // If path already starts with /details/, rewrite to include locale
    if (imgPath.startsWith("/details/")) {
      const baseName = imgPath.replace("/details/", "");
      return `/details/${locale}/${baseName}`;
    }
    return imgPath;
  });

  // Locale-aware text helpers
  const getText = (field) => {
    if (!product) return "";
    const value = product[field];
    if (typeof value === "object" && value !== null) {
      return value[locale] ?? value.ko ?? "";
    }
    return value ?? "";
  };

  const getFeatures = () => {
    if (!product) return [];
    const feats = product.features;
    if (typeof feats === "object" && feats !== null && Array.isArray(feats[locale])) {
      return feats[locale];
    }
    if (Array.isArray(feats)) return feats;
    return [];
  };

  const productName = getText("name");
  const productDescription = getText("description");
  const productHeadline = getText("headline");
  const productDetail = getText("detail");
  const productFeatures = getFeatures();

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [productKey]);

  const handlePrevImage = () => {
    if (!detailImages.length) {
      return;
    }

    setCurrentImageIndex((prev) => (prev === 0 ? detailImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!detailImages.length) {
      return;
    }

    setCurrentImageIndex((prev) => (prev === detailImages.length - 1 ? 0 : prev + 1));
  };

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4 text-white">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-zinc-900 p-8 text-center shadow-2xl shadow-black/30">
          <p className="text-xl font-bold">{labels.notFoundTitle}</p>
          <button
            onClick={() => navigate("/payment")}
            className="mt-6 inline-flex h-10 items-center justify-center rounded-xl bg-white px-4 text-sm font-semibold text-zinc-950 transition-colors hover:bg-sky-100"
          >
            {labels.backToList}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.2),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.16),transparent_34%),linear-gradient(180deg,#09090b_0%,#020617_100%)]" />

      <header className="sticky top-0 z-40 border-b border-white/10 bg-zinc-950/75 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-zinc-950">
              <Tag className="h-5 w-5" />
            </div>
            <span className="text-lg font-semibold tracking-tight">TMTT.Link</span>
            <span className="ml-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-medium text-zinc-300">
              {labels.label}
            </span>
          </div>
          <button
            onClick={toggleLocale}
            title={locale === "ko" ? "Switch to English" : "한국어로 전환"}
            className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 text-sm font-medium text-zinc-200 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Globe className="h-4 w-4" />
            <span className="font-medium">{locale === "ko" ? "EN" : "KO"}</span>
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <button
          onClick={() => navigate("/payment")}
          className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {labels.backButton}
        </button>

        <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-zinc-900/80 shadow-2xl shadow-black/20">
            <div className="relative aspect-[891/1260] w-full bg-zinc-800">
              <img
                src={detailImages[currentImageIndex]}
                alt={`${productName} ${labels.imgAlt} ${currentImageIndex + 1}`}
                className="h-full w-full object-cover"
              />

              {detailImages.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/55 text-white transition-colors hover:bg-black/75"
                    aria-label={labels.ariaPrev}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/55 text-white transition-colors hover:bg-black/75"
                    aria-label={labels.ariaNext}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="absolute bottom-3 right-3 rounded-full border border-white/10 bg-black/55 px-3 py-1 text-xs text-zinc-200">
                    {currentImageIndex + 1} / {detailImages.length}
                  </div>
                </>
              )}
            </div>

            {detailImages.length > 1 && (
              <div className="grid grid-cols-3 gap-3 border-t border-white/10 bg-black/20 p-4">
                {detailImages.map((image, index) => (
                  <button
                    key={image}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`relative overflow-hidden rounded-2xl border transition-all ${
                      currentImageIndex === index ? "border-sky-300 opacity-100" : "border-white/10 opacity-65 hover:opacity-100"
                    }`}
                  >
                    <div className="aspect-[891/1260] w-full bg-zinc-800">
                      <img
                        src={image}
                        alt={`${productName} ${labels.thumbAlt} ${index + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-zinc-900/80 p-6 shadow-2xl shadow-black/20 md:p-8">
            <div className="mb-4 inline-flex rounded-full border border-sky-400/20 bg-sky-400/10 px-3 py-1 text-sm font-medium text-sky-200">
              {productDescription}
            </div>
            <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">{productName}</h1>
            <p className="mt-5 text-base leading-7 text-zinc-300 md:text-lg">{productHeadline}</p>
            <p className="mt-4 text-sm leading-7 text-zinc-400 md:text-base">{productDetail}</p>

            <div className="mt-7 rounded-3xl border border-white/10 bg-white/[0.04] p-5">
              <p className="text-sm font-semibold text-zinc-100">{labels.featuresTitle}</p>
              <div className="mt-4 space-y-3 text-sm text-zinc-300">
                {productFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-300"></span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-7 rounded-3xl border border-white/10 bg-white p-5 text-zinc-950">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-zinc-500">{labels.priceLabel}</p>
                  <p className="mt-1 text-3xl font-semibold">{formatPrice(product.price, locale)}</p>
                </div>
                <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700">
                  {labels.infoText}
                </span>
              </div>
              <a
                href={product.naverUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-zinc-950 px-5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800"
              >
                <ShoppingCart className="h-4 w-4" />
                <span>{labels.payButton}</span>
                <ExternalLink className="h-4 w-4" />
              </a>
              <p className="mt-3 text-center text-xs text-zinc-500">{labels.storeNote}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
